import React, { useEffect, useState, type HTMLAttributes } from "react";
import { clsx } from "clsx";
import { parseResources, toResourceCards, type Data, GetList } from "@module/blog";
import { useStore } from "@nanostores/react";
import { useTranslatedRelative } from "@i18next/client/utils";
// widget
import Pagination from "@react/widget/Pagination";
import BlogsCard from "@react/widget/BlogsCard";
import Img from "@react/widget/Img";
import { Chip } from "@nextui-org/chip";
import { H2 } from "@react/widget/Headline";
import Button from "@react/widget/Button";
import { excludes, sort, topic } from "@module/blog/store";

interface Props extends HTMLAttributes<HTMLDivElement> {
    currentSite: Page.Site;
    init?: Data;
    label: Record<string, string>;
}

var first = true;

const Cards: React.FC<Props> = ({currentSite, className, init = {entryCount: 0, entries: []}, children, label, ...props}) => {
    const tr = useTranslatedRelative(currentSite.locale);
    const $excludes = useStore(excludes);
    const $topic = useStore(topic);
    const $sort = useStore(sort);
    const [loading, setLoading] = useState(false);
    const limit = 18;
    const [page, setPage] = useState(1);
    const [total, setTotal] = useState(Math.ceil(init.entryCount / limit));
    const [entries, setEntries] = useState(parseResources(init.entries));
    //console.log("init entryCount:", init.entryCount);
    const render = () => {
        setLoading(true);
        GetList({
            locale: currentSite.locale,
            excludes: $excludes,
            topic: $topic, // 将更新后的 topic 作为参数传入@module/blog 下的 Getlist
            sort: $sort,
            page: page,
            limit: limit,
        }, (res) => {
            setTotal(Math.ceil(res.entryCount / limit));
            setEntries(parseResources(res.entries)); // 更新 entries 的内容
            setLoading(false);
            //console.log("entryCount:", res.entryCount);
        }, () => {
            setLoading(false);
        });
    }
    useEffect(() => {
        if(!first) {
            if(page === 1) render();
            else setPage(1);
        }
    }, [$topic, $sort]);
    useEffect(() => {
        if(!first) {
            render();
        }
    }, [page]);
    useEffect(() => {first = false;}, []);
    if(!entries || !entries.length) {
        return <h4 className="text-center w-full">{label?.nodata}</h4>;
    } else {
        return <>
            <div className={clsx("grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-4 gap-y-20", className)} {...props}>
                { entries?.map((entry, i) => {
                  return <BlogsCard isLoaded={!loading} key={`blogs-${i}`} blogFeaturedImage={(
                      <a href={tr(entry.uri)} className={clsx("block absolute inset-0 rounded-t-lg overflow-hidden")}>
                              <Img className={clsx("w-full h-full object-cover transition duration-500 absolute inset-0")}
                                  src={entry.blogFeaturedImage}
                                  width={389}
                                  height={218}
                                  fit={"cover"}
                              />
                      </a>
                  )} topics={entry.topics?.map(t => (
                      <a key={t.slug} href={tr(`/blog/category/${t.slug}`)}>
                          <Chip
                              size="sm"
                              variant="flat"
                              classNames={{
                                  base: `rounded-md border-1 transition hover:grayscale`,
                              }}
                              style={{
                                  borderColor: t.color,
                                  color: t.color,
                                  backgroundColor: `${t.color}0f`,
                              }}
                              >
                              {t.title}
                          </Chip>
                      </a>
                  ))} summary={(
                      <a href={tr(entry.uri)}>
                          <H2
                              size="lg"
                              className="hover:text-primary transition duration-500 line-clamp-2"
                              title={entry.title}
                          >{entry.title}
                          </H2>
                      </a>
                  )} tags={entry.tags?.slice(0, 3).map(t => (
                    <a key={t.slug} href={tr(t.uri)}>
                      <Chip
                          key={t.slug}
                          size="sm"
                          variant="faded"
                          classNames={{
                          base: `rounded-md bg-transparent capitalize hover:bg-gray-60 transition duration-500`,
                          }}
                      >
                          {t.title}
                      </Chip>
                    </a>
                  ))}
                      cta={(
                      <Button
                          className="!px-0 !justify-start !pb-0 mt-4"
                          size="lg"
                          href={tr(entry.uri)}
                          icon="arrow-right-long"
                      >{label.read}</Button>
                  )}
                      className="max-w-sm md:max-w-full mx-auto w-full [&:hover_img]:scale-110 rounded-lg overflow-hidden"></BlogsCard>
                }).filter(e => !!e)}
            </div>
            <Pagination label={label} page={page} total={total} onChange={p => {setPage(p); toResourceCards()}}/>
        </>;
    }
}

export default Cards;